import type { RootState } from '..'
import type { AuthTokens } from '../types/auth'

import { baseApi } from '.'

const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        body: credentials,
      }),
    }),
    logout: builder.mutation({
      query: (credentials) => ({
        url: '/auth/logout',
        method: 'POST',
        body: credentials,
      }),
    }),
    loginAs: builder.mutation<AuthTokens, any>({
      query: (body) => ({
        url: `/auth/login-as`,
        method: 'POST',
        body: body,
      }),
    }),
    registerWithCredentials: builder.mutation({
      query: (credentials) => ({
        url: '/auth/register',
        method: 'POST',
        body: credentials,
      }),
    }),
    getGoogleLoginLink: builder.query({
      query: () => ({
        url: '/auth/link/google',
        method: 'GET',
      }),
    }),
    getLinkedinLoginLink: builder.query({
      query: () => ({
        url: '/auth/link/linkedin',
        method: 'GET',
      }),
    }),

    loginWithGoogle: builder.mutation<AuthTokens, { code: string }>({
      query: (credentials) => ({
        url: '/auth/login/google',
        method: 'POST',
        body: credentials,
      }),
    }),
    loginWithLinkedin: builder.mutation<AuthTokens, { code: string }>({
      query: (credentials) => ({
        url: '/auth/login/linkedin',
        method: 'POST',
        body: credentials,
      }),
    }),

    shopifyRegister: builder.mutation({
      query: (credentials) => ({
        url: '/auth/shopify-register',
        method: 'POST',
        body: credentials,
      }),
    }),
    registerWithGoogle: builder.mutation({
      query: (credentials) => ({
        url: '/auth/register/google',
        method: 'POST',
        body: credentials,
      }),
    }),
    registerWithLinkedin: builder.mutation({
      query: (credentials) => ({
        url: '/auth/register/linkedin',
        method: 'POST',
        body: credentials,
      }),
    }),
    resetPassword: builder.mutation({
      query: (credentials) => ({
        url: '/auth/reset-password',
        method: 'POST',
        body: credentials,
      }),
    }),
    setPassword: builder.mutation({
      query: (credentials) => ({
        url: '/auth/set-password',
        method: 'POST',
        body: credentials,
      }),
    }),
    sendRefreshToken: builder.mutation<
      any,
      {
        refresh_token: string | null
      }
    >({
      query: (body) => ({
        url: 'auth/refresh',
        method: 'POST',
        body: {
          refresh_token: body.refresh_token,
        },
      }),
    }),
  }),
})

export const userOnboardingCompletedSelector = (state: RootState) => {
  const data = authApi.endpoints.me.select()(state)?.data
  if (!data) {
    return false
  }
  return data.is_onboarding_completed
}

export const userIsUrlBasedSelector = (state: RootState) => {
  const data = authApi.endpoints.me.select()(state)?.data
  if (!data) {
    return false
  }
  return data.monitoring_source === 'PRISYNC_ENGINE'
}

export const userIsChannelBasedSelector = (state: RootState) => {
  const data = authApi.endpoints.me.select()(state)?.data
  if (!data) {
    return false
  }
  return data.monitoring_source === 'DATAEDIS'
}

export const userIsHybridSelector = (state: RootState) => {
  const data = authApi.endpoints.me.select()(state)?.data
  if (!data) {
    return false
  }
  return data.monitoring_source === 'HYBRID'
}

export const {
  useLoginMutation,
  useLoginWithGoogleMutation,
  useLoginWithLinkedinMutation,
  useLogoutMutation,
  useLoginAsMutation,
  useRegisterWithCredentialsMutation,
  useRegisterWithGoogleMutation,
  useRegisterWithLinkedinMutation,
  useResetPasswordMutation,
  useSetPasswordMutation,
  useLazyGetGoogleLoginLinkQuery,
  useLazyGetLinkedinLoginLinkQuery,
  useSendRefreshTokenMutation,
  useShopifyRegisterMutation,
} = authApi
